import formatBlendshapes from '@/pages/DigitalHuman/util/formatBlendshapes'
import { getDigitalStore } from '@/store/digital'
import audioManager from '@/utils/audioManager'
import { generateTTS, createTTSParams, validateTTSText } from '@/services/ttsService'
import { AnimationClip, LoopOnce, LoopRepeat, NumberKeyframeTrack } from 'three'
import { ScenicDataInsights,DriveDigitalHumanSpeak } from '@/store/types'

export interface TTSData {
  text: string
  url: string
  index: number
  messageId: string
  bs: string
}

class TTSPlayerManage {
  private static instance: TTSPlayerManage
  private constructor() { }
  // 是否接收完毕TTS数据
  isReceived = false
  // TTS数据
  ttsData: TTSData[] = []
  // 当前播放的索引
  private currentIndex = 0
  // 是否正在播放
  isPlaying = false
  // 等待timer
  private waitTimer: NodeJS.Timeout | null = null
  // 等待计数器，避免无限等待
  private waitCount = 0
  // 最大等待次数（等待5秒后跳过）
  private maxWaitCount = 5
  // 音频结束回调
  private onEndCallback = () => {
    // 当前音频播放完毕，播放下一段
    this.currentIndex++
    this.playNext()
  }
  private bsActions: Map<string, THREE.AnimationAction> = new Map(); // 存储每个 mesh 对应的 action


  public static getInstance(): TTSPlayerManage {
    if (!TTSPlayerManage.instance) {
      TTSPlayerManage.instance = new TTSPlayerManage()
    }
    return TTSPlayerManage.instance
  }

  public start() {
    // 重置播放器
    this.reset()
    this.isReceived = false
    this.currentIndex = 0
    this.isPlaying = false
    this.clearCurrentSound()
    // 开始播放流程
    this.playNext()
  }

  // 清除当前音频
  private clearCurrentSound() {
    audioManager.stop()

    if (this.waitTimer) {
      clearTimeout(this.waitTimer)
      this.waitTimer = null
    }
  }

  // 播放下一段音频
  private playNext() {
    // 获取当前索引的数据
    const currentData = this.ttsData.find(item => item.index === this.currentIndex)

    if (currentData) {
      // 有数据，播放当前数据
      this.waitCount = 0 // 重置等待计数器
      this.playSound(currentData)
      getDigitalStore().update((state) => {
        state.isSpeaking = true
      })
    }
    else {
      // 当前索引没有数据，先检查后面是否还有其他数据
      const nextAvailableData = this.findNextAvailableData()
      
      if (nextAvailableData) {
        // 后面有数据，说明当前索引可能丢失了，直接跳到下一个可用索引
        console.log(`索引 ${this.currentIndex} 数据缺失，跳转到索引 ${nextAvailableData.index}`)
        this.currentIndex = nextAvailableData.index
        this.waitCount = 0 // 重置等待计数器
        this.playNext()
        return
      }
      else if (!this.isReceived) {
        // 后面没有数据且未接收完毕，说明还在接收过程中，需要等待
        console.log(`等待索引 ${this.currentIndex} 的数据... (${this.waitCount + 1}/${this.maxWaitCount})`)
        
        if (this.waitCount >= this.maxWaitCount) {
          // 等待超时，跳过当前索引继续尝试下一个
          console.warn(`索引 ${this.currentIndex} 等待超时，跳过到下一个索引`)
          this.currentIndex++
          this.waitCount = 0
          this.playNext()
          return
        }
        
        this.waitCount++
        this.waitTimer = setTimeout(() => {
          this.playNext()
        }, 1000)
      }
      else {
        // 已接收完毕且没有更多数据，播放结束
        console.log('所有音频播放完毕')
        this.isPlaying = false
        getDigitalStore().update((state) => {
          state.isSpeaking = false
        })
        this.reset()
      }
    }
  }

  // 查找下一个可用的数据
  private findNextAvailableData(): TTSData | undefined {
    return this.ttsData
      .filter(item => item.index > this.currentIndex)
      .sort((a, b) => a.index - b.index)[0]
  }

  // 播放指定的音频数据
  private playSound(data: TTSData) {
    this.clearCurrentSound()
    // 使用 audioManager 播放音频
    audioManager.play([data.url])

    // 播放bs
    this.playBSAnimation(data.bs)


    // 设置音频结束事件监听
    audioManager.removeEventListener('onend', this.onEndCallback)
    audioManager.addEventListener('onend', this.onEndCallback)

    // 设置音频加载错误事件监听
    audioManager.addEventListener('onloaderror', (id, error) => {
      console.error(`音频加载错误 [${data.index}]: ${error}`, id)
      // 出错时也尝试播放下一段
      this.currentIndex++
      this.playNext()
    })

    // 设置音频播放错误事件监听
    audioManager.addEventListener('onplayerror', (id, error) => {
      console.error(`音频播放错误 [${data.index}]: ${error}`, id)
      // 出错时也尝试播放下一段
      this.currentIndex++
      this.playNext()
    })


    this.isPlaying = true
    console.log(`正在播放索引 ${data.index} 的音频: ${data.text}`)
    getDigitalStore().update((state) => {
      state.isPlayIndex = data.index
    })

  }

  // 停止播放
  public stop() {
    console.log('停止播放')
    this.stopBSAnimation()
    this.clearCurrentSound()
    this.isPlaying = false
    getDigitalStore().update((state) => {
      state.isSpeaking = false
    })
  }

  // 重置播放器
  public reset() {
    this.stop()
    this.ttsData = []
    this.currentIndex = 0
    this.isReceived = false
    this.waitCount = 0 // 重置等待计数器
    audioManager.removeEventListener('onend', this.onEndCallback)
    audioManager.addEventListener('onend', () => {})
  }

  /**
 * 播放 bs 动画
 */
  public playBSAnimation = async (bs: string) => {
    if (!bs) {
      bs = 'https://s.newscdn.cn/xihu/bs.txt'
    }
    try { 
      // 停止所有之前的动画
      this.bsActions.forEach((action, meshName) => {
        action.stop();
      });
      this.bsActions.clear(); // 清空之前的 action 记录

      const formattedData = await formatBlendshapes(bs);

      getDigitalStore().morphMeshes.forEach((mesh) => {
        const tracks: any[] = [];
        Object.keys(formattedData[0].blendshapes).forEach((blendshapeKey) => {
          const times = formattedData.map((item) => item.time);
          const values = formattedData.map((item) => item.blendshapes[blendshapeKey]);

          tracks.push(new NumberKeyframeTrack(`${mesh.name}.morphTargetInfluences[${blendshapeKey}]`, times, values));
        });

        const clip = new AnimationClip('animation', -1, tracks);

        if (getDigitalStore().mixer && mesh) {
          const action = getDigitalStore().mixer?.clipAction(clip, mesh);
          action?.setLoop(LoopRepeat, 0);  // 设置动画只播放一次
          action?.play();  // 播放新动画

          // 保存当前 mesh 的 action 以便以后停止
          if (action) {
            // @ts-ignore
            this.bsActions.set(mesh.name, action);
          }
        }
      });
    } catch (error) {
      console.error('播放 bs 动画失败', error)
    }
  };

  // 停止 bs 动画
  private stopBSAnimation = () => {
    this.bsActions.forEach((action, meshName) => {
      action.stop();
    });
    this.bsActions.clear(); // 清空之前的 action 记录
  }
}

export const ttsPlayerManage = TTSPlayerManage.getInstance()

export const driveDigitalHumanByText = async (text: string) => {
  // 验证输入文本
  const validation = validateTTSText(text)
  if (!validation.valid) {
    console.warn('TTS文本验证失败:', validation.message)
    return
  }

  try {
    // 重置TTS播放器
    ttsPlayerManage.reset()
    
    console.log('开始调用TTS API生成语音数据...', { text })
    const driveDigitalDate = Date.now().toString()
    getDigitalStore().update((state) => {
      state.driveDigitalDate = driveDigitalDate
    })
    // 调用TTS API
    const ttsParams = createTTSParams(text)
    const response = await generateTTS(ttsParams);

    if (getDigitalStore().driveDigitalDate !== driveDigitalDate) {
      console.log('检测到有新的驱动请求，停止此次驱动', response)
      return;
    }
    
    if (getDigitalStore().isSpacePressed) {
      console.log('用户按下了空格键，停止TTS播放')
      return;
    }
    
    console.log('TTS API调用成功，开始处理数据...', response.data)
    
    // 创建TTS数据对象
    const ttsData: TTSData = {
      text: response.data.text,
      url: response.data.tts,
      index: 0,
      messageId: ttsParams.id,
      bs: response.data.bs
    }
    
    console.log('触发了agent分派')
    window.chat_setMessages([{
      role: 'assistant',
      content: ttsData.text,
    }])
    
    // 添加到TTS播放器
    getDigitalStore().update((state) => {
      state.isSpeaking = true
    })
    audioManager.play([ttsData.url], () => {
      getDigitalStore().update((state) => {
        state.isSpeaking = false
      })
    })
    

    audioManager.removeEventListener('onend', () => {})
    audioManager.addEventListener('onend', () => {})

    ttsPlayerManage.playBSAnimation(ttsData.bs)
  } catch (error) {
    console.error('驱动数字人失败:', error)    
  }
}

export const driveDigitalHumanByPreset = async (options: {
  showText: string;
  ttsUrl: string;
  bsUrl?: string;
}) => {
  if (!options.showText) {
    console.warn('文本验证失败:')
    return
  }

  try {
    // 重置TTS播放器
    ttsPlayerManage.reset()
    
    // 创建TTS数据对象
    const ttsData: TTSData = {
      text: options?.showText,
      url: options.ttsUrl,
      index: 0,
      messageId: Date.now().toString(),
      bs: options.bsUrl || ''
    }
    console.log('触发了agent分派')
    window.chat_setMessages([{
      role: 'assistant',
      content: ttsData.text,
    }])
    getDigitalStore().update((state) => {
      state.isSpeaking = true
      state.driveDigitalDate = Date.now().toString()
    })
    
    // 添加到TTS播放器
    // audioManager.play([ttsData.url])
    audioManager.play([ttsData.url], () => {
      getDigitalStore().update((state) => {
        state.isSpeaking = false
      })
    })
    

    audioManager.removeEventListener('onend', () => {})
    audioManager.addEventListener('onend', () => {})

    ttsPlayerManage.playBSAnimation(ttsData?.bs)
  } catch (error) {
    console.error('驱动数字人失败:', error)    
  }
}


export const driveDigitalHumanBySpeak = async (speak: DriveDigitalHumanSpeak) => {
  setTimeout(()=>{
    driveDigitalHumanByPreset({
      showText: speak.text,
      ttsUrl: speak.tts,
      bsUrl: speak.bs 
    })
  },20)
}

// export const driveDigitalHumanByScenicDataInsights = async (data?: ScenicDataInsights) => {  
//   const speak = data?.find((item) => item.type === 'xihu_insights')
//   if(speak){
//     driveDigitalHumanByPreset({
//       showText: speak.text,
//       ttsUrl: speak.tts,
//       bsUrl: speak.bs
//     })
//   }else{
//     console.warn('没有找到xihu_insights数据',data);
//   }
// }




